import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import api from 'api';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export const useRelatedGoals = ({
  clientId,
  criticalElementId,
  enteringClassYear,
  isEditMode,
  onSave,
}) => {
  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const [isLoading, setIsLoading] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const [linkedData, setLinkedData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [initialLinkedGoalsIds, setInitialLinkedGoalsIds] = useState([]);
  const [pendingGoalChanges, setPendingGoalChanges] = useState({});

  const goalsLoadedRef = useRef(false);
  const linkedGoalsLoadedRef = useRef(false);
  const initialLoadCompletedRef = useRef(false);

  const [filterOption, setFilterOption] = useState(
    criticalElementId ? 'linked' : 'all'
  );

  useEffect(() => {
    setFilterOption(criticalElementId ? 'linked' : 'all');
  }, [criticalElementId]);

  const effectiveLinkedGoalIds = useMemo(() => {
    const result = [...initialLinkedGoalsIds];

    Object.values(pendingGoalChanges).forEach(({ goal, action }) => {
      const index = result.indexOf(goal.id);

      if (action === 'link' && index === -1) {
        result.push(goal.id);
      } else if (action === 'unlink' && index !== -1) {
        result.splice(index, 1);
      }
    });

    return result;
  }, [initialLinkedGoalsIds, pendingGoalChanges]);

  const fetchLinkedGoals = useCallback(async () => {
    if (!criticalElementId || !userId) {
      setLinkedData([]);
      setInitialLinkedGoalsIds([]);

      return;
    }

    if (linkedGoalsLoadedRef.current) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.getAllCriticalElementGoals({
        userId,
        criticalElementId,
      });

      const linkedGoals = response?.data?.content || response?.data || [];
      const linkedIds = linkedGoals.map((goal) => goal.id);

      setLinkedData(linkedGoals);
      setInitialLinkedGoalsIds(linkedIds);
      setPendingGoalChanges({});

      linkedGoalsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching linked goals', error);
      setLinkedData([]);
      setInitialLinkedGoalsIds([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, criticalElementId]);

  const fetchAllGoals = useCallback(async () => {
    if (!userId || !clientId) return;

    if (goalsLoadedRef.current) {
      return;
    }

    setIsLoading(true);
    try {
      const yearToFetch = enteringClassYear || new Date().getFullYear();

      const response = await api.getAllGoals({
        userId,
        clientId,
        year: yearToFetch,
      });

      const goals = response?.data?.content || response?.data || [];

      setAllData(goals);

      goalsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching all goals', error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, clientId, enteringClassYear]);

  const handleFilterChange = useCallback((option) => {
    setFilterOption(option);
  }, []);

  const handleToggleChange = useCallback(
    (goal, newToggleValue) => {
      if (!isEditMode) return;

      setPendingGoalChanges((prev) => {
        const wasInitiallyLinked = initialLinkedGoalsIds.includes(goal.id);
        const action = newToggleValue ? 'link' : 'unlink';

        if (
          (newToggleValue && wasInitiallyLinked) ||
          (!newToggleValue && !wasInitiallyLinked)
        ) {
          const { [goal.id]: _, ...rest } = prev;

          return rest;
        }

        return { ...prev, [goal.id]: { goal, action } };
      });
    },
    [isEditMode, initialLinkedGoalsIds]
  );

  const applyPendingChanges = useCallback(
    async (newCriticalElementId) => {
      const targetId = newCriticalElementId || criticalElementId;

      if (!targetId || !userId) return;

      setIsToggling(true);
      try {
        for (const { goal, action } of Object.values(pendingGoalChanges)) {
          if (action === 'link') {
            await api.updateCriticalElementGoal({
              userId,
              criticalElementId: targetId,
              goalId: goal.id,
              goalData: goal,
            });
          } else if (action === 'unlink') {
            await api.deleteCriticalElementGoal({
              userId,
              criticalElementId: targetId,
              goalId: goal.id,
            });
          }
        }

        setInitialLinkedGoalsIds(effectiveLinkedGoalIds);
        setPendingGoalChanges({});

        if (newCriticalElementId) {
          linkedGoalsLoadedRef.current = false;
          await fetchLinkedGoals();
        }
      } catch (error) {
        console.error('Error applying goal changes', error);
        await fetchLinkedGoals();
      } finally {
        setIsToggling(false);
      }
    },
    [
      userId,
      criticalElementId,
      pendingGoalChanges,
      effectiveLinkedGoalIds,
      fetchLinkedGoals,
    ]
  );

  const currentData = useMemo(() => {
    if (filterOption === 'linked') {
      return (allData.length > 0 ? allData : linkedData).filter((goal) =>
        effectiveLinkedGoalIds.includes(goal.id)
      );
    }

    return allData;
  }, [filterOption, linkedData, allData, effectiveLinkedGoalIds]);

  useEffect(() => {
    if (onSave) {
      onSave.current = applyPendingChanges;
    }
  }, [onSave, applyPendingChanges]);

  useEffect(() => {
    if (initialLoadCompletedRef.current) {
      return;
    }

    const loadData = async () => {
      await fetchAllGoals();

      if (criticalElementId) {
        await fetchLinkedGoals();
      }

      initialLoadCompletedRef.current = true;
    };

    loadData();
  }, []);

  useEffect(() => {
    goalsLoadedRef.current = false;
    linkedGoalsLoadedRef.current = false;
    initialLoadCompletedRef.current = false;
  }, [clientId, enteringClassYear]);

  useEffect(() => {
    if (criticalElementId) {
      linkedGoalsLoadedRef.current = false;
    }
  }, [criticalElementId]);

  useEffect(() => {
    if (!isEditMode) {
      setPendingGoalChanges({});
    }
  }, [isEditMode]);

  return {
    filterOption,
    handleFilterChange,
    currentData,
    linkedGoalsIds: effectiveLinkedGoalIds,
    isCreation: !criticalElementId && isEditMode,
    showLoader: isLoading || isToggling,
    isEditMode,
    handleToggleChange,
    isToggling,
  };
};

export default useRelatedGoals;
